import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Occupancy,
  Override,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DAY_OVERRIDE_MODAL_TAB_IDS,
  type DayOverrideModalTabIds,
  MIN_LOS_DEFAULT_VALUE,
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  OVERRIDE_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
  STAY_RESTRICTIONS_RATE_GROUP_IDS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  RateGroupContextFields,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import {
  calculateTotalRoomsForSale,
  getOverrideVariant,
} from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import {
  hasRestrictionActionChanged,
  mergeRestrictionActionsByContext,
  minLosRestrictionValues,
  minLosTypeToValue,
  minLosValueToType,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';
import {
  formatMinLosRecommendation,
  formatMinLosValue,
  formatOverbookingValue,
  minLosErrorMessage,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './day-override-modal.module.css';
import OverbookingPanel from './overbooking-panel';
import Sidebar, { type SidebarItem } from './sidebar';
import type { StayRestrictionsAccordionState } from './stay-restrictions-accordion';
import StayRestrictionsPanel from './stay-restrictions-panel';

interface HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature {
  Args: {
    onCloseModal: () => void;
    period: Period;
  };
  Blocks: {
    default: [];
  };
}

type SendRestrictionConfig = RateGroupContextFields & {
  existingOverride: MinLosOverride | OverbookingOverride | null | undefined;
  getRestrictionValues: () => RestrictionActionValue[] | null;
  hasError: () => boolean;
  markTouched: () => void;
  overrideRemoved: boolean;
  overrideType: RestrictionOverridePayload['type'];
  overrideValue: number | null;
  restriction: Restriction | undefined;
  restrictionRecommendation: RestrictionRecommendation | undefined;
};

export default class DayOverrideModal extends Component<HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTabId: DayOverrideModalTabIds =
    DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;

  @tracked deepDiscountMinLosAutomationOnUserEdit?: boolean;
  @tracked deepDiscountMinLosRestrictionType: MinLosRestrictionType =
    MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
  @tracked deepDiscountMinLosTouched = false;
  @tracked deepDiscountMinLosUserEdit?: number | null;

  @tracked hotelMinLosAutomationOnUserEdit?: boolean;
  @tracked hotelMinLosRestrictionType: MinLosRestrictionType =
    MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
  @tracked hotelMinLosTouched = false;
  @tracked hotelMinLosUserEdit?: number | null;

  @tracked overbookingAutomationOnUserEdit?: boolean;
  @tracked overbookingTouched = false;
  @tracked overbookingUserEdit?: number | null;

  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking deepDiscountMinLosOverrideRemoved mid-send. */
  initiallyHadDeepDiscountMinLosOverride: boolean = false;
  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking hotelMinLosOverrideRemoved mid-send. */
  initiallyHadHotelMinLosOverride: boolean = false;
  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking overbookingOverrideRemoved mid-send. */
  initiallyHadOverbookingOverride: boolean = false;

  get deepDiscountMinLos(): number | null {
    return this.resolveUserEdit(
      this.deepDiscountMinLosUserEdit,
      this.deepDiscountMinLosSeedValue,
    );
  }

  get deepDiscountMinLosAutomationOn(): boolean {
    return this.resolveUserEdit(
      this.deepDiscountMinLosAutomationOnUserEdit,
      this.deepDiscountRestriction?.minLosRestrictionValueObject
        ?.autopilotEnabled ?? false,
    );
  }

  /* True when the Deep Discount accordion's sendable state differs from what is in
     place, i.e. the user actually changed it. */
  get deepDiscountMinLosChanged(): boolean {
    return this.minLosChanged(
      this.deepDiscountMinLosAutomationOn,
      this.deepDiscountMinLosRestrictionType,
      this.deepDiscountMinLos,
      this.deepDiscountRestriction,
      this.deepDiscountMinLosSeedValue,
    );
  }

  get deepDiscountMinLosErrorMessage(): string | null {
    return this.visibleMinLosErrorMessage(
      this.deepDiscountMinLosTouched,
      this.deepDiscountMinLosAutomationOn,
      this.deepDiscountMinLosRestrictionType,
      this.deepDiscountMinLos,
    );
  }

  get deepDiscountMinLosOverrideRemoved(): boolean {
    return (
      this.deepDiscountMinLosAutomationOn &&
      this.initiallyHadDeepDiscountMinLosOverride
    );
  }

  /* Seed value for the Deep Discount MinLOS input and radio type. hasDeepDiscountMinLosOverride
     is already false during a failed push (the failed override is treated as absent), so the
     input and radio fall back to the MIN_LOS default. */
  private get deepDiscountMinLosSeedValue(): number {
    return (
      (this.args.period.hasDeepDiscountMinLosOverride
        ? this.deepDiscountRestriction?.minLosOverride?.value
        : null) ?? MIN_LOS_DEFAULT_VALUE
    );
  }

  get deepDiscountMinLosState(): StayRestrictionsAccordionState {
    return {
      automationOn: this.deepDiscountMinLosAutomationOn,
      currentLabel: this.formattedDeepDiscountMinLosCurrentLabel,
      errorMessage: this.deepDiscountMinLosErrorMessage,
      failedValue: this.args.period.failedDeepDiscountMinLosValue,
      formattedValue: this.formattedDeepDiscountMinLosValue,
      hasOverride: this.args.period.hasDeepDiscountMinLosOverride,
      isLoading: this.isAccordionLoading(
        this.args.period.hasDeepDiscountMinLosActionInProgress,
        this.deepDiscountMinLosOverrideRemoved,
        this.deepDiscountMinLosWillSend,
      ),
      onBlur: (): void => this.onDeepDiscountMinLosBlur(),
      onTypeChange: (event: Event): void =>
        this.onDeepDiscountMinLosTypeChange(event),
      restrictionType: this.deepDiscountMinLosRestrictionType,
      showException: this.showDeepDiscountRestrictionException,
      toggleAutomation: (event: Event): void =>
        this.toggleDeepDiscountMinLosAutomation(event),
      touched: this.deepDiscountMinLosTouched,
      updateValue: (value: number | null): void =>
        this.updateDeepDiscountMinLos(value),
      value: this.deepDiscountMinLos,
      warningMessage: this.deepDiscountMinLosWarningMessage,
    };
  }

  /* Warning message when hotel MinLOS takes precedence over DD MinLOS */
  get deepDiscountMinLosWarningMessage(): string | null {
    // An invalid value shows its error first; don't stack the precedence warning
    // (and its amber input border) on top of the error state.
    if (this.deepDiscountMinLosErrorMessage) {
      return null;
    }

    if (
      this.deepDiscountMinLosAutomationOn ||
      // Hotel set to "No MinLos restriction" has no MinLOS to take precedence.
      (!this.hotelMinLosAutomationOn &&
        this.hotelMinLosRestrictionType !== MIN_LOS_RESTRICTION_TYPE.MIN_LOS)
    ) {
      return null;
    }

    // Compare against the hotel MinLOS that is (or will be) in force after save, so the warning always matches the Hotel accordion:
    // - automation on:  the effective hotel value (any override is dropped on save)
    // - automation off: the live hotel MinLOS input (the user's edit, or the existing override it was seeded from)
    const hotelMinLosToCompare = this.hotelMinLosAutomationOn
      ? this.args.period.effectiveMinLos
      : this.hotelMinLos;

    // "No MinLos restriction" means no Deep Discount floor (MinLOS 0), so the hotel
    // always takes precedence. The number input preserves its last value on type
    // change, so derive the effective value from the type rather than the input.
    const deepDiscountMinLosToCompare = minLosTypeToValue(
      this.deepDiscountMinLosRestrictionType,
      this.deepDiscountMinLos,
    );

    // Tradeoff: elsewhere (e.g. the tooltip) which MinLOS applies is read from the
    // backend's applied_context on the restriction / restriction-recommendation. That
    // isn't available for the user's unsaved edits, so we derive precedence here on the FE.
    if (
      deepDiscountMinLosToCompare === null ||
      hotelMinLosToCompare === null ||
      deepDiscountMinLosToCompare >= hotelMinLosToCompare
    ) {
      return null;
    }

    return this.intl.t(
      'pricing_manager.restrictions.deep_discount_below_hotel_warning',
      { count: hotelMinLosToCompare },
    );
  }

  get deepDiscountMinLosWillSend(): boolean {
    return this.willSend(
      this.deepDiscountMinLosChanged,
      this.deepDiscountMinLosAutomationOn,
      this.deepDiscountMinLosOverrideRemoved,
    );
  }

  get deepDiscountRestriction(): Restriction | undefined {
    return this.args.period.deepDiscountRestriction;
  }

  get deepDiscountRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.deepDiscountRestrictionRecommendation;
  }

  get formattedDeepDiscountMinLosCurrentLabel(): string {
    return this.intl.t('pricing_manager.restrictions.current_value', {
      value: formatMinLosValue({
        intl: this.intl,
        value: this.deepDiscountRestriction?.minLosValue,
      }),
    });
  }

  get formattedDeepDiscountMinLosValue(): string {
    return formatMinLosRecommendation({
      displayedValue: this.deepDiscountRestriction?.minLosValue,
      intl: this.intl,
      recommendedValue: this.deepDiscountRestrictionRecommendation?.minLosValue,
    });
  }

  get formattedHotelMinLosCurrentLabel(): string {
    return this.intl.t('pricing_manager.restrictions.current_value', {
      value: formatMinLosValue({
        intl: this.intl,
        value: this.hotelLevelRestriction?.minLosValue,
      }),
    });
  }

  get formattedHotelMinLosValue(): string {
    return formatMinLosRecommendation({
      displayedValue: this.hotelLevelRestriction?.minLosValue,
      intl: this.intl,
      recommendedValue: this.hotelLevelRestrictionRecommendation?.minLosValue,
    });
  }

  get formattedOverbookingValue(): number | string {
    return formatOverbookingValue({
      intl: this.intl,
      value: this.args.period.effectiveOverbooking,
    });
  }

  get hasConfiguredDeepDiscountRateGroup(): boolean {
    return (
      this.hasDeepDiscountAutopilotToggle &&
      this.isDeepDiscountRateGroupConfigured === true
    );
  }

  private get hasDeepDiscountAutopilotToggle(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_DEEP_DISCOUNT_AUTOPILOT,
    );
  }

  get hasRestrictionsTabToggle(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.DAY_OVERRIDE_MODAL_RESTRICTIONS_TAB,
    );
  }

  get hasStayRestrictionsOverride(): boolean {
    return (
      this.args.period.hasMinLosOverride ||
      (this.hasConfiguredDeepDiscountRateGroup &&
        this.args.period.hasDeepDiscountMinLosOverride)
    );
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.period.hotelLevelRestriction;
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.hotelLevelRestrictionRecommendation;
  }

  get hotelMinLos(): number | null {
    return this.resolveUserEdit(
      this.hotelMinLosUserEdit,
      this.hotelMinLosSeedValue,
    );
  }

  get hotelMinLosAutomationOn(): boolean {
    return this.resolveUserEdit(
      this.hotelMinLosAutomationOnUserEdit,
      this.hotelLevelRestriction?.minLosRestrictionValueObject
        ?.autopilotEnabled ?? false,
    );
  }

  /* True when the Hotel MinLOS accordion's sendable state differs from what is in
     place, i.e. the user actually changed it. */
  get hotelMinLosChanged(): boolean {
    return this.minLosChanged(
      this.hotelMinLosAutomationOn,
      this.hotelMinLosRestrictionType,
      this.hotelMinLos,
      this.hotelLevelRestriction,
      this.hotelMinLosSeedValue,
    );
  }

  get hotelMinLosErrorMessage(): string | null {
    return this.visibleMinLosErrorMessage(
      this.hotelMinLosTouched,
      this.hotelMinLosAutomationOn,
      this.hotelMinLosRestrictionType,
      this.hotelMinLos,
    );
  }

  get hotelMinLosOverrideRemoved(): boolean {
    return this.hotelMinLosAutomationOn && this.initiallyHadHotelMinLosOverride;
  }

  /* Seed value for the Hotel MinLOS input and radio type. hasMinLosOverride is already
     false during a failed push (the failed override is treated as absent), so the input
     and radio fall back to the MIN_LOS default. */
  private get hotelMinLosSeedValue(): number {
    return (
      (this.args.period.hasMinLosOverride
        ? this.hotelLevelRestriction?.minLosOverride?.value
        : null) ?? MIN_LOS_DEFAULT_VALUE
    );
  }

  get hotelMinLosState(): StayRestrictionsAccordionState {
    return {
      automationOn: this.hotelMinLosAutomationOn,
      currentLabel: this.formattedHotelMinLosCurrentLabel,
      errorMessage: this.hotelMinLosErrorMessage,
      failedValue: this.args.period.failedHotelMinLosValue,
      formattedValue: this.formattedHotelMinLosValue,
      hasOverride: this.args.period.hasMinLosOverride,
      isLoading: this.isAccordionLoading(
        this.args.period.hasMinLosActionInProgress,
        this.hotelMinLosOverrideRemoved,
        this.hotelMinLosWillSend,
      ),
      onBlur: (): void => this.onHotelMinLosBlur(),
      onTypeChange: (event: Event): void => this.onHotelMinLosTypeChange(event),
      restrictionType: this.hotelMinLosRestrictionType,
      showException: this.showHotelRestrictionException,
      toggleAutomation: (event: Event): void =>
        this.toggleHotelMinLosAutomation(event),
      touched: this.hotelMinLosTouched,
      updateValue: (value: number | null): void =>
        this.updateHotelMinLos(value),
      value: this.hotelMinLos,
    };
  }

  get hotelMinLosWillSend(): boolean {
    return this.willSend(
      this.hotelMinLosChanged,
      this.hotelMinLosAutomationOn,
      this.hotelMinLosOverrideRemoved,
    );
  }

  /* undefined until the subscription-meta request has resolved. */
  private get isDeepDiscountRateGroupConfigured(): boolean | undefined {
    return this.pricingAssistantDataRequestService
      .requestPricingAssistantSubscriptionMetaTask.lastSuccessful?.value
      ?.isDeepDiscountRateGroupConfigured;
  }

  get isOverbooked(): boolean {
    return (
      typeof this.physicalRoomsAvailable === 'number' &&
      this.physicalRoomsAvailable < 0
    );
  }

  get isOverbookingLoading(): boolean {
    return this.isAccordionLoading(
      this.args.period.hasOverbookingActionInProgress,
      this.overbookingOverrideRemoved,
      this.overbookingWillSend,
    );
  }

  /* The pre-push window shared across accordions: the override delete and the
  subsequent restrictions/recommendations re-request. Not attributable to a single
  context, so each accordion gates it on its own `overrideRemoved` getter. */
  get isPreparingSend(): boolean {
    return (
      this.pricingAssistantDataRequestService.deleteOverridesTask.isRunning ||
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning
    );
  }

  /* True while the restriction-action push POST is in flight, before the action record
  lands in the store and starts driving the `has*ActionInProgress` getters. */
  get isPushingRestrictionAction(): boolean {
    return this.restrictionsDataRequestService.createRestrictionActionTask
      .isRunning;
  }

  get isSending(): boolean {
    const isActionInProgress =
      this.args.period.hasOverbookingActionInProgress ||
      this.args.period.hasMinLosActionInProgress ||
      this.args.period.hasDeepDiscountMinLosActionInProgress;

    return (
      isActionInProgress ||
      this.isPushingRestrictionAction ||
      this.isPreparingSend
    );
  }

  get modalTitle(): string {
    const carate = this.args.period.carates?.[0];
    const day = carate?.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    return `${this.intl.t('general.edit')} ${day}, ${this.intl.t('pricing_assistant.all_rooms')}`;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  get overbooking(): number | null {
    return this.resolveUserEdit(
      this.overbookingUserEdit,
      this.args.period.effectiveOverbookingWithOverride,
    );
  }

  get overbookingAutomationOn(): boolean {
    return this.resolveUserEdit(
      this.overbookingAutomationOnUserEdit,
      this.args.period.hotelLevelRestriction?.overbookingRestrictionValueObject
        ?.autopilotEnabled ?? false,
    );
  }

  /* True when the overbooking accordion's sendable state differs from what is in
     place, i.e. the user actually changed it. */
  get overbookingChanged(): boolean {
    return hasRestrictionActionChanged(
      {
        automationOn: this.overbookingAutomationOn,
        value: this.overbooking,
      },
      {
        automationOn:
          this.hotelLevelRestriction?.overbookingRestrictionValueObject
            ?.autopilotEnabled ?? false,
        value: this.args.period.effectiveOverbookingWithOverride,
      },
    );
  }

  /* Returns `undefined` (not `null` like the MinLOS error getters) because this
     value is passed straight to PrismNumber's `@errorMessage` prop, which is typed
     `string | undefined`. The MinLOS getters instead feed a boolean `@hasErrors` and
     render their own text, so they follow the `minLosErrorMessage` util's null convention. */
  get overbookingErrorMessage(): string | undefined {
    if (!this.overbookingTouched || this.overbookingAutomationOn) {
      return undefined;
    }

    return this.overbooking === null
      ? this.intl.t('general.required')
      : undefined;
  }

  /* True when the user has toggled automation ON from an initial OFF state,
     meaning the existing override will be deleted on Send. */
  get overbookingOverrideRemoved(): boolean {
    return this.overbookingAutomationOn && this.initiallyHadOverbookingOverride;
  }

  get overbookingWillSend(): boolean {
    return this.willSend(
      this.overbookingChanged,
      this.overbookingAutomationOn,
      this.overbookingOverrideRemoved,
    );
  }

  get physicalRoomsAvailable(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking.physical_rooms_available_tooltip',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking.physical_rooms_available_tooltip',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get roomsForSaleValue(): string | number {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking ?? 0, // Treat empty overbooking (null) as 0 for calculation purposes
      );
    }

    return this.intl.t('general.missing');
  }

  get sendButtonLabel(): string {
    const label = this.intl.t('pricing_assistant.label.push');
    const count = this.getPendingConfigs().length;
    return count > 0 ? `${label} (${count})` : label;
  }

  get showDeepDiscountAccordion(): boolean {
    return this.hasDeepDiscountAutopilotToggle;
  }

  /* Empty state when the Deep Discount rate group is not configured — also shown in
     the edge case where the meta request that reports the configuration fails. */
  get showDeepDiscountEmptyState(): boolean {
    return (
      this.showDeepDiscountAccordion &&
      this.isDeepDiscountRateGroupConfigured !== true
    );
  }

  get showDeepDiscountRestrictionException(): boolean {
    return (
      this.args.period.shouldShowStayRestrictionException &&
      this.args.period.hasFailedDeepDiscountMinLos
    );
  }

  get showHotelRestrictionException(): boolean {
    return (
      this.args.period.shouldShowStayRestrictionException &&
      this.args.period.hasFailedHotelMinLos
    );
  }

  get showOverbookingException(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverbookingOverride(): boolean {
    return (
      this.args.period.hasOverbookingOverride && !this.showOverbookingException
    );
  }

  get showOverbookingTab(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_OVERBOOKING_AUTOPILOT,
      ) && this.access.hasPricingAssistantOverbookingRestrictionsFeature
    );
  }

  get showStayRestrictionsTab(): boolean {
    return (
      this.hasRestrictionsTabToggle &&
      this.access.hasPricingAssistantMinLosRestrictionsFeature
    );
  }

  get sidebarItems(): SidebarItem[] {
    const items: SidebarItem[] = [];

    if (this.showOverbookingTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING,
        hasException: this.showOverbookingException,
        hasOverride: this.showOverbookingOverride,
        label: this.intl.t('pricing_manager.overbooking.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.OVERBOOKING),
      });
    }

    if (this.showStayRestrictionsTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS,
        hasException: this.args.period.shouldShowStayRestrictionException,
        hasOverride: this.hasStayRestrictionsOverride,
        label: this.intl.t('pricing_manager.restrictions.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.MIN_LOS),
      });
    }

    return items;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature['Args'],
  ) {
    super(owner, args);
    // COA-3693 TODO: remove once COA-3687 is resolved.
    // BE still sends the failed override value when a push fails, showing an exception instead.
    // Treat the override as absent so overbookingOverrideRemoved stays correct if the exception clears before send.
    if (this.showOverbookingException) {
      this.initiallyHadOverbookingOverride = false;
    } else {
      this.initiallyHadOverbookingOverride =
        this.args.period.hasOverbookingOverride;
    }
    this.initiallyHadHotelMinLosOverride = this.args.period.hasMinLosOverride;
    this.resetHotelMinLosState();
    this.initiallyHadDeepDiscountMinLosOverride =
      this.args.period.hasDeepDiscountMinLosOverride;
    this.resetDeepDiscountMinLosState();
    this.activeTabId =
      this.sidebarItems[0]?.id ?? DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
  }

  private getDeepDiscountMinLosRestrictionValues():
    | RestrictionActionValue[]
    | null {
    return minLosRestrictionValues(
      this.deepDiscountMinLosOverrideRemoved,
      this.deepDiscountRestrictionRecommendation?.minLosValue,
      this.deepDiscountMinLosRestrictionType,
      this.deepDiscountMinLos,
    );
  }

  private getHotelMinLosRestrictionValues(): RestrictionActionValue[] | null {
    return minLosRestrictionValues(
      this.hotelMinLosOverrideRemoved,
      this.hotelLevelRestrictionRecommendation?.minLosValue,
      this.hotelMinLosRestrictionType,
      this.hotelMinLos,
    );
  }

  private getOverbookingRestrictionValues(): RestrictionActionValue[] | null {
    if (this.overbookingOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.overbookingValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.OVERBOOKING, value: recommendationValue }]
        : null;
    }

    if (this.overbooking == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.OVERBOOKING, value: this.overbooking }];
  }

  private getPendingConfigs(): SendRestrictionConfig[] {
    const configs: SendRestrictionConfig[] = [];

    if (this.showOverbookingTab && this.overbookingWillSend) {
      configs.push({
        context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        existingOverride: this.hotelLevelRestriction?.overbookingOverride,
        restriction: this.hotelLevelRestriction,
        restrictionRecommendation: this.hotelLevelRestrictionRecommendation,
        overrideRemoved: this.overbookingOverrideRemoved,
        overrideValue: this.overbooking,
        overrideType: OVERRIDE_TYPES.OVERBOOKING,
        getRestrictionValues: () => this.getOverbookingRestrictionValues(),
        hasError: () => this.overbookingErrorMessage !== undefined,
        markTouched: () => {
          this.overbookingTouched = true;
        },
      });
    }

    if (this.showStayRestrictionsTab && this.hotelMinLosWillSend) {
      const overrideValue = minLosTypeToValue(
        this.hotelMinLosRestrictionType,
        this.hotelMinLos,
      );
      configs.push({
        context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        existingOverride: this.hotelLevelRestriction?.minLosOverride,
        restriction: this.hotelLevelRestriction,
        restrictionRecommendation: this.hotelLevelRestrictionRecommendation,
        overrideRemoved: this.hotelMinLosOverrideRemoved,
        overrideValue,
        overrideType: OVERRIDE_TYPES.MIN_LOS,
        getRestrictionValues: () => this.getHotelMinLosRestrictionValues(),
        hasError: () => this.hotelMinLosErrorMessage !== null,
        markTouched: () => {
          this.hotelMinLosTouched = true;
        },
      });
    }

    if (
      this.showStayRestrictionsTab &&
      this.hasConfiguredDeepDiscountRateGroup &&
      this.deepDiscountMinLosWillSend
    ) {
      const overrideValue = minLosTypeToValue(
        this.deepDiscountMinLosRestrictionType,
        this.deepDiscountMinLos,
      );
      configs.push({
        context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
        existingOverride: this.deepDiscountRestriction?.minLosOverride,
        restriction: this.deepDiscountRestriction,
        restrictionRecommendation: this.deepDiscountRestrictionRecommendation,
        overrideRemoved: this.deepDiscountMinLosOverrideRemoved,
        overrideValue,
        overrideType: OVERRIDE_TYPES.MIN_LOS,
        rateGroupId: STAY_RESTRICTIONS_RATE_GROUP_IDS.DEEP_DISCOUNT,
        getRestrictionValues: () =>
          this.getDeepDiscountMinLosRestrictionValues(),
        hasError: () => this.deepDiscountMinLosErrorMessage !== null,
        markTouched: () => {
          this.deepDiscountMinLosTouched = true;
        },
      });
    }

    return configs;
  }

  /* The three checks map to the three phases a Send goes through, each gated on the
     accordion's own signals so a sibling's action never drives it:
     1. `overrideRemoved && isPreparingSend` — deleting the override (automation
        checkbox ticked) and the shared restrictions/recommendations re-request.
     2. `willSend && isPushingRestrictionAction` — the createRestrictionAction POST is
        in flight; `hasActionInProgress` is not active yet since it relies on the
        restriction-action record landing in the store.
     3. `hasActionInProgress` — the restriction-action has landed and its pollers have
        started. */
  private isAccordionLoading(
    hasActionInProgress: boolean,
    overrideRemoved: boolean,
    willSend: boolean,
  ): boolean {
    return (
      (overrideRemoved && this.isPreparingSend) ||
      (willSend && this.isPushingRestrictionAction) ||
      hasActionInProgress
    );
  }

  /* True when an accordion's sendable MinLOS state differs from what is in place.
     Shared by the Hotel and Deep Discount accordions, which compare identically.
     The in-place value must be the accordion's seed value, not the raw
     `minLosOverride.value`: during a failed push the BE workaround reports the
     override as absent so the input seeds to MIN_LOS_DEFAULT_VALUE, while the
     restriction still carries the failed value. Baselining against the seed keeps
     the comparison in step with what the input shows, so an untouched failed
     override is not read as a pending change. */
  private minLosChanged(
    automationOn: boolean,
    restrictionType: MinLosRestrictionType,
    minLos: number | null,
    restriction: Restriction | undefined,
    inPlaceValue: number,
  ): boolean {
    return hasRestrictionActionChanged(
      {
        automationOn,
        type: restrictionType,
        value: minLosTypeToValue(restrictionType, minLos),
      },
      {
        automationOn:
          restriction?.minLosRestrictionValueObject?.autopilotEnabled ?? false,
        type: minLosValueToType(inPlaceValue),
        value: inPlaceValue,
      },
    );
  }

  private resetDeepDiscountMinLosState(): void {
    this.deepDiscountMinLosUserEdit = undefined;
    this.deepDiscountMinLosRestrictionType = minLosValueToType(
      this.deepDiscountMinLosSeedValue,
    );
  }

  private resetHotelMinLosState(): void {
    this.hotelMinLosUserEdit = undefined;
    this.hotelMinLosRestrictionType = minLosValueToType(
      this.hotelMinLosSeedValue,
    );
  }

  /* Returns the user's edit when present, otherwise the value from the API.
    `undefined` means "not edited"; a `null`/`false` edit is a real value the user set, so
     we test against `undefined` rather than falling back with `??`. */
  private resolveUserEdit<T>(userEdit: T | undefined, liveValue: T): T {
    return userEdit === undefined ? liveValue : userEdit;
  }

  private async sendAllPendingOverrides(
    configs: SendRestrictionConfig[],
  ): Promise<'success' | 'error' | null> {
    const formattedDate = this.args.period.fromDate.format('YYYY-MM-DD');
    const hotelId = this.hotelAndMarketService.hotel.id!;
    const hasRemovals = configs.some((c) => c.overrideRemoved);

    const overrideIdsToDelete = configs
      .filter((c) => c.overrideRemoved && c.existingOverride)
      .map((c) => c.existingOverride!.id);

    if (overrideIdsToDelete.length > 0) {
      const result =
        await this.pricingAssistantDataRequestService.deleteOverridesTask.perform(
          overrideIdsToDelete,
        );
      if (!result?.isSuccess) {
        return 'error';
      }
    }

    if (hasRemovals) {
      /* EmberData keeps a previously-loaded record when the re-request response omits it,
         so a restriction/recommendation that disappears after a removal would linger.
         Unload the removed contexts' records so the re-request rebuilds them cleanly.
         Untouched contexts are always returned, so leaving them loaded strands nothing and
         keeps the other accordion from flipping its automation control mid-re-request. */
      const recordsToUnload = new Set(
        configs
          .filter((c) => c.overrideRemoved)
          .flatMap((c) => [c.restriction, c.restrictionRecommendation])
          .filter((r) => r != null),
      );

      recordsToUnload.forEach((r) => this.store.unloadRecord(r));

      await Promise.all([
        this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
        this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
      ]);
    }

    const overrides: RestrictionOverridePayload[] = [];
    for (const config of configs) {
      const shouldSendOverride = !config.overrideRemoved;
      if (shouldSendOverride && config.overrideValue !== null) {
        overrides.push({
          subscription_id: parseInt(hotelId, 10),
          from_date: formattedDate,
          value: config.overrideValue,
          type: config.overrideType,
          context: config.context,
          rate_group_id: config.rateGroupId ?? null,
        });
      }
    }

    this.restrictionsDataRequestService.overrideCallbackOnPushFinished = async (
      day: string,
      subscriptionId: string,
    ): Promise<void> => {
      this.store
        .peekAll<Override>('override')
        .filter((o: Override) => o.fromDate.format('YYYY-MM-DD') === day)
        .forEach((o: Override) => this.store.unloadRecord(o));

      await this.pricingAssistantDataRequestService.requestOverridesTask.perform(
        subscriptionId,
        day,
        day,
      );
    };

    /* Construct one restriction_action_config per context by merging the config
       objects that belong to the same context. */
    const restrictionActionData = mergeRestrictionActionsByContext(
      configs,
      formattedDate,
    );

    /* Empty when no config yielded a value to send: a removal whose re-request returned no
       recommendation, or a send with no value entered. */
    if (restrictionActionData.length === 0) {
      return hasRemovals ? 'success' : null;
    }

    /* DEEP DISCOUNT TODO: this will be cleaned up in a follow-up ticket (COA-3860) */
    const isSingleHotelLevelConfig =
      restrictionActionData.length === 1 &&
      restrictionActionData[0]!.context ===
        RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL;

    try {
      if (isSingleHotelLevelConfig) {
        await this.restrictionsDataRequestService.sendRestriction(
          restrictionActionData[0]!,
          RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
          overrides,
        );
      } else {
        await this.restrictionsDataRequestService.sendRestriction(
          restrictionActionData,
          undefined,
          overrides,
        );
      }

      return 'success';
    } catch {
      return 'error';
    }
  }

  /* Shared by both accordions: an untouched or automated input shows no error. */
  private visibleMinLosErrorMessage(
    touched: boolean,
    automationOn: boolean,
    restrictionType: MinLosRestrictionType,
    value: number | null,
  ): string | null {
    if (!touched || automationOn) {
      return null;
    }

    return minLosErrorMessage({
      intl: this.intl,
      restrictionType,
      value,
    });
  }

  /* True when an accordion's change will be sent on the next push, covering both a
     removal and a value change with automation off. Shared by all three accordions and
     is the single source consulted by getPendingConfigs. */
  private willSend(
    changed: boolean,
    automationOn: boolean,
    overrideRemoved: boolean,
  ): boolean {
    return changed && (!automationOn || overrideRemoved);
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  onDeepDiscountMinLosBlur(): void {
    this.deepDiscountMinLosTouched = true;
  }

  @action
  onDeepDiscountMinLosTypeChange(event: Event): void {
    const type = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
    this.deepDiscountMinLosRestrictionType = type;
  }

  @action
  onHotelMinLosBlur(): void {
    this.hotelMinLosTouched = true;
  }

  @action
  onHotelMinLosTypeChange(event: Event): void {
    const type = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
    /* hotelMinLos is intentionally not updated — the number input value is preserved
       so switching back to MIN_LOS restores whatever the user had typed before. */
    this.hotelMinLosRestrictionType = type;
  }

  @action
  onOverbookingBlur(): void {
    this.overbookingTouched = true;
  }

  @action
  async onSend(): Promise<void> {
    if (this.isSending) {
      return;
    }

    const configs = this.getPendingConfigs();

    if (configs.length === 0) {
      this.onCloseModal();
      return;
    }

    /* Set pending configs' input fields touched so blocking errors are shown. */
    configs.forEach((config) => config.markTouched());
    if (configs.some((config) => config.hasError())) {
      return;
    }

    for (const config of configs) {
      this.usageAnalytics.track(
        config.overrideRemoved
          ? 'Autopilot Override Disabled'
          : 'Autopilot Override Enabled',
        {
          eventTrigger: 'day-override-modal',
          product: 'pricing-manager',
          overrideType:
            config.overrideType === OVERRIDE_TYPES.MIN_LOS
              ? 'min_los'
              : 'overbooking',
        },
      );
    }

    const outcome = await this.sendAllPendingOverrides(configs);

    if (outcome === 'success') {
      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } else if (outcome === 'error') {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }

    this.onCloseModal();
  }

  @action
  selectTab(id: DayOverrideModalTabIds): void {
    this.activeTabId = id;
  }

  @action
  toggleDeepDiscountMinLosAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.deepDiscountMinLosAutomationOnUserEdit = checked;
    this.resetDeepDiscountMinLosState();

    if (!checked) {
      this.deepDiscountMinLosTouched = false;
    }
  }

  @action
  toggleHotelMinLosAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.hotelMinLosAutomationOnUserEdit = checked;
    this.resetHotelMinLosState();

    if (!checked) {
      this.hotelMinLosTouched = false;
    }
  }

  @action
  toggleOverbookingAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.overbookingAutomationOnUserEdit = checked;

    if (checked && this.initiallyHadOverbookingOverride) {
      // Turning automation ON when override exists — show recommendation value
      this.overbookingUserEdit = this.args.period.effectiveOverbooking;
    } else {
      // Toggling off, or on with no override — drop any user edit
      this.overbookingUserEdit = undefined;
      this.overbookingTouched = false;
    }
  }

  @action
  updateDeepDiscountMinLos(value: number | null): void {
    this.deepDiscountMinLosUserEdit = value;
  }

  @action
  updateHotelMinLos(value: number | null): void {
    this.hotelMinLosUserEdit = value;
  }

  @action
  updateOverbooking(value: number | null): void {
    this.overbookingUserEdit = value;
  }

  <template>
    <PrismModal
      @onClose={{this.onCloseModal}}
      @title={{this.modalTitle}}
      class={{styles.modal}}
    >
      <:default>
        <div class={{styles.body}}>
          <div class="text-300--regular">
            <Sidebar
              @activeTabId={{this.activeTabId}}
              @items={{this.sidebarItems}}
              @onSelect={{this.selectTab}}
            />
          </div>
          <div>
            {{#if (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING)}}
              <OverbookingPanel
                @automationOn={{this.overbookingAutomationOn}}
                @effectiveOverbookingWithOverride={{@period.effectiveOverbookingWithOverride}}
                @errorMessage={{this.overbookingErrorMessage}}
                @failedOverbookingValue={{@period.failedOverbookingValue}}
                @formattedOverbooking={{this.formattedOverbookingValue}}
                @hasOverbookingOverride={{this.showOverbookingOverride}}
                @isLoading={{this.isOverbookingLoading}}
                @isOverbooked={{this.isOverbooked}}
                @isTouched={{this.overbookingTouched}}
                @onOverbookingBlur={{this.onOverbookingBlur}}
                @onToggleAutomation={{this.toggleOverbookingAutomation}}
                @onUpdateOverbooking={{this.updateOverbooking}}
                @overbooking={{this.overbooking}}
                @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
                @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
                @roomsForSaleValue={{this.roomsForSaleValue}}
                @showOverbookingException={{this.showOverbookingException}}
              />
            {{else if
              (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS)
            }}
              <StayRestrictionsPanel
                @deepDiscountState={{this.deepDiscountMinLosState}}
                @hotelState={{this.hotelMinLosState}}
                @showDeepDiscount={{this.showDeepDiscountAccordion}}
                @showDeepDiscountEmptyState={{this.showDeepDiscountEmptyState}}
              />
            {{/if}}
          </div>
        </div>
      </:default>
      <:footer>
        <div
          class="mx-500 flex h-full flex-row items-center justify-end gap-200"
        >
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onCloseModal}}
          />
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isLoading={{this.isSending}}
            @label={{this.sendButtonLabel}}
            @onClick={{this.onSend}}
            data-test-id="day-override-send-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
